import { z } from "zod";
import { useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { AlertTriangle, Check, X } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const FormConfirmSchema = z.object({
  action: z.string(),
  description: z.string(),
  fields: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      editable: z.boolean().optional(),
    }),
  ),
  confirmLabel: z.string().optional(),
  cancelLabel: z.string().optional(),
});

type FormConfirmData = z.infer<typeof FormConfirmSchema>;

/* ────────────────────── Component ────────────────────── */

export function FormConfirm({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<FormConfirmData>) {
  const { action, description, fields, confirmLabel, cancelLabel } =
    widget.data;

  const [fieldValues, setFieldValues] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    fields.forEach((f, idx) => {
      if (f.editable) init[idx] = f.value;
    });
    return init;
  });

  function handleConfirm() {
    if (!onDeepLink) return;
    onDeepLink(`confirm:${action}`);
  }

  function handleCancel() {
    if (!onDeepLink) return;
    onDeepLink(`cancel:${action}`);
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-amber-200 shadow-sm",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3 bg-amber-50 px-4 py-3 dark:bg-amber-950/30">
        <div className="mt-0.5 shrink-0 rounded-full bg-amber-100 p-1.5 dark:bg-amber-900/40">
          <AlertTriangle className="size-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-amber-900 dark:text-amber-200">
            실행 확인 요청
          </p>
          <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">
            {description}
          </p>
        </div>
      </div>

      {/* Fields */}
      {fields.length > 0 && (
        <div className="divide-y divide-border px-4 py-2">
          {fields.map((field, idx) => (
            <div key={idx} className="flex items-center gap-3 py-2">
              <span className="w-28 shrink-0 text-[11px] font-medium text-text-tertiary">
                {field.label}
              </span>
              {field.editable ? (
                <input
                  type="text"
                  value={fieldValues[idx] ?? field.value}
                  onChange={(e) =>
                    setFieldValues((prev) => ({
                      ...prev,
                      [idx]: e.target.value,
                    }))
                  }
                  className={cn(
                    "flex-1 rounded-md border border-stroke-subtle bg-surface-base px-2 py-1",
                    "text-[12px] text-text-primary",
                    "focus:outline-none focus:ring-1 focus:ring-accent/40",
                  )}
                />
              ) : (
                <span className="flex-1 text-[12px] font-medium text-text-primary">
                  {field.value}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 border-t border-stroke-subtle bg-surface-secondary/20 px-4 py-3">
        <button
          onClick={handleCancel}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border border-stroke-subtle bg-surface-base px-3 py-1.5",
            "text-[12px] font-medium text-text-primary",
            "transition-colors hover:bg-surface-secondary/60",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
          )}
        >
          <X className="size-3.5" />
          {cancelLabel ?? "취소"}
        </button>
        <button
          onClick={handleConfirm}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5",
            "text-[12px] font-medium text-text-inverse",
            "transition-colors hover:bg-accent/90",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
          )}
        >
          <Check className="size-3.5" />
          {confirmLabel ?? "확인 및 실행"}
        </button>
      </div>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function FormConfirmSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-stroke-subtle",
        className,
      )}
    >
      {/* Header skeleton */}
      <div className="flex items-start gap-3 bg-surface-secondary/30 px-4 py-3">
        <Skeleton className="mt-0.5 size-7 shrink-0 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3 w-56" />
        </div>
      </div>
      {/* Fields skeleton */}
      <div className="divide-y divide-border px-4 py-2">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <Skeleton
              className="h-3 w-24 shrink-0"
              style={{ animationDelay: `${i * 80}ms` }}
            />
            <Skeleton
              className="h-7 flex-1 rounded-md"
              style={{ animationDelay: `${i * 80 + 40}ms` }}
            />
          </div>
        ))}
      </div>
      {/* Actions skeleton */}
      <div className="flex justify-end gap-2 border-t border-stroke-subtle bg-surface-secondary/20 px-4 py-3">
        <Skeleton className="h-8 w-16 rounded-lg" />
        <Skeleton className="h-8 w-28 rounded-lg" />
      </div>
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const formConfirmWidget = defineWidget<FormConfirmData>({
  type: WIDGET_TYPES.FORM_CONFIRM,
  label: "Write 도구 승인 요청",
  schema: FormConfirmSchema,
  component: FormConfirm,
  skeleton: FormConfirmSkeleton,
  defaultLayout: "full",
  maxHeight: 320,
  aiHint:
    "데이터 변경·삭제·전송 등 Write 도구 실행 전 사용자 승인을 요청하는 확인 폼",
});

registerWidget(formConfirmWidget);
